import { useEffect } from 'react';
import {
  isAddFrShortcut,
  isAddPartsShortcut,
  isEditDamageShortcut,
  isSelectNextLineShortcut,
  isSelectPreviousLineShortcut,
  isTypingTarget,
} from './preTimingShortcuts';

type UsePreTimingGridShortcutsArgs = {
  enabled: boolean;
  saving: boolean;
  popupOpen: boolean;
  canAddFr: boolean;
  canAddParts: boolean;
  canEditDamage: boolean;
  selectedId?: number;
  visibleIds: number[];
  /** Displayed row order. Falls back to visibleIds when omitted. */
  getVisibleIds?: () => number[];
  onSelect: (id: number) => void;
  onAddFr: () => void;
  onAddParts: () => void;
  onEditDamage: () => void;
};

const neighborId = (
  visibleIds: number[],
  selectedId: number | undefined,
  step: 1 | -1,
) => {
  if (!visibleIds.length) return undefined;
  const currentIndex = selectedId == null ? -1 : visibleIds.indexOf(selectedId);
  const nextIndex =
    currentIndex === -1
      ? step === 1
        ? 0
        : visibleIds.length - 1
      : currentIndex + step;
  if (nextIndex < 0 || nextIndex >= visibleIds.length) return selectedId;
  return visibleIds[nextIndex];
};

export const usePreTimingGridShortcuts = ({
  enabled,
  saving,
  popupOpen,
  canAddFr,
  canAddParts,
  canEditDamage,
  selectedId,
  visibleIds,
  getVisibleIds,
  onSelect,
  onAddFr,
  onAddParts,
  onEditDamage,
}: UsePreTimingGridShortcutsArgs) => {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (saving) {
        if (
          isAddFrShortcut(event) ||
          isAddPartsShortcut(event) ||
          isEditDamageShortcut(event) ||
          isSelectNextLineShortcut(event) ||
          isSelectPreviousLineShortcut(event)
        ) {
          event.preventDefault();
        }
        return;
      }

      if (isAddFrShortcut(event)) {
        event.preventDefault();
        if (!popupOpen && canAddFr && selectedId != null) onAddFr();
        return;
      }

      if (isAddPartsShortcut(event)) {
        event.preventDefault();
        if (!popupOpen && canAddParts) onAddParts();
        return;
      }

      if (isEditDamageShortcut(event)) {
        event.preventDefault();
        if (!popupOpen && canEditDamage && selectedId != null) onEditDamage();
        return;
      }

      if (popupOpen || isTypingTarget(event.target)) return;

      const orderedIds = getVisibleIds?.() ?? visibleIds;

      if (isSelectNextLineShortcut(event)) {
        event.preventDefault();
        const nextId = neighborId(orderedIds, selectedId, 1);
        if (nextId != null) onSelect(nextId);
        return;
      }

      if (isSelectPreviousLineShortcut(event)) {
        event.preventDefault();
        const nextId = neighborId(orderedIds, selectedId, -1);
        if (nextId != null) onSelect(nextId);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    canAddFr,
    canAddParts,
    canEditDamage,
    enabled,
    onAddFr,
    onAddParts,
    onEditDamage,
    onSelect,
    getVisibleIds,
    popupOpen,
    saving,
    selectedId,
    visibleIds,
  ]);
};
